import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { success, failure } from '~/redux/actions/snackbar-actions';
import { createTag, fetchTags } from '~/redux/actions/tags-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { DEFAULT_PAGINATION } from '~/utils/constants';

// MUI Components
import Typography from '@mui/material/Typography';
import { Box, InputLabel, TextField, CircularProgress, InputAdornment } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// icons
import { TemplateCrossCIcon } from '~/assets/images';

// styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const CreateTagModal = (props) => {
  const formId = 'create-tags-form';
  const {
    openModal,
    handleCloseModal,
    setSelectedTags,
    setTags = false,
    tagsClass = false,
    updatePagination,
    onLimitReached,
  } = props;

  const [loading, setLoading] = useState(false);
  const [tagsToCreate, setTagsToCreate] = useState([]);
  const [customErrors, setCustomErrors] = useState([]);

  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    name: yup.string().test('check-count', MESSAGES.SETTINGS.TAGS.CREATE.TAG_LIMIT, (value) => {
      if (!value) return true;

      const tags = value.split(',');
      for (const tag of tags) {
        if (tag.trim().length > 35) {
          return false;
        }
      }
      return true;
    }),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: validationSchema,
  });

  const handleEnter = (e, enter = false) => {
    if ((enter || e.key === 'Enter') && formik.values.name.trim() !== '') {
      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      const tags = [
        ...new Set([
          ...tagsToCreate,
          ...formik.values.name
            .trim()
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
        ]),
      ];
      setTagsToCreate(tags);
      setCustomErrors([]);
      formik.handleReset();
    }
  };

  const handleChipsCross = (e) => {
    const changedSet = tagsToCreate.filter((el) => el != e.target.alt);
    setTagsToCreate(changedSet);
  };

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
    setTagsToCreate([]);
    setCustomErrors([]);
    setLoading(false);
  };

  const createNewTag = async (event) => {
    try {
      event.preventDefault();
      handleEnter(event, true);
      let concatedValue = null;
      if (Object.keys(formik.errors).length < 1 && tagsToCreate.length == 0) {
        if (formik.values.name.length) {
          concatedValue = formik.values.name
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
        } else {
          return setCustomErrors([MESSAGES.SETTINGS.TAGS.TAGS_REQUIRED]);
        }
      }

      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      setLoading(true);
      const payload = {
        tags: tagsToCreate.length ? tagsToCreate : concatedValue,
      };
      const response = await dispatch(createTag(payload));
      if (response.status === 418) {
        if (onLimitReached) {
          const errorMessage = response?.data?.message || '';
          onClose();
          onLimitReached(errorMessage);
        }
        return;
      }
      if (response.status === 200) {
        updatePagination && updatePagination(DEFAULT_PAGINATION);
        dispatch(fetchTags(DEFAULT_PAGINATION));
        dispatch(success(response?.data?.message));
        const createdTags = response?.data?.data || [];
        if (createdTags.length) {
          if (setTags) {
            setSelectedTags((prev) => [...prev, ...createdTags.map((tag) => tag.title)]);
          } else if (tagsClass) {
            setSelectedTags([...createdTags]); // Ensure it's a new array reference
          }
        }
        onClose();
      } else {
        setLoading(false);
        dispatch(failure(response?.data?.message));
      }
    } catch (error) {
      setLoading(false);
      setTagsToCreate([]);

      // For other errors, dispatch failure message
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    }
  };

  useEffect(() => {
    if (customErrors.length > 0) {
      setCustomErrors([]);
    }
  }, [formik.values.name]);

  useEffect(() => {
    if (!openModal) {
      // Reset all state when modal closes
      formik.handleReset();
      resetValidation();
      setTagsToCreate([]);
      setCustomErrors([]);
      setLoading(false);
    }
  }, [openModal]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.SETTINGS.TAGS.CREATE.TITLE}
      className={`createTagModalWrapper ${tagsClass ? 'tagsClassBackDrop' : ''}`}
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            {MESSAGES.SETTINGS.TAGS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form={formId}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              MESSAGES.SETTINGS.TAGS.CREATE.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="modalFormWrapper">
        <form
          id={formId}
          className="updateFormWrapper"
          onSubmit={createNewTag}
          noValidate
        >
          <Box className="modalFlexArea">
            <Box
              sx={{
                width: '100%',
                marginInline: 'auto',
                textAlign: 'start',
              }}
            >
              <InputLabel htmlFor="first-name" className="inputLabel formLabel">
                {MESSAGES.SETTINGS.TAGS.CREATE.TAG_LABEL}
                <span className="olcRequired">*</span>
              </InputLabel>
              <TextField
                placeholder={MESSAGES.SETTINGS.TAGS.CREATE.TAG_PLACEHOLDER}
                fullWidth
                name="name"
                autoComplete="off"
                onChange={formik.handleChange}
                className={
                  (formik.errors.name && formik.touched.name) || customErrors.length > 0
                    ? 'invalid formInput inputField formField'
                    : 'inputField formField'
                }
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                  handleEnter(e);
                }}
                value={formik.values.name.trimStart()}
                sx={{
                  '& input::placeholder': {
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                  },
                  outline: 'none',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                      {formik.values.name && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue('name', '');
                            formik.setFieldTouched('name', true);
                          }}
                          edge="end"
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.name && formik.errors.name ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {formik.errors.name}
                </Typography>
              ) : null}
              {customErrors.length > 0 ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {customErrors}
                </Typography>
              ) : null}
            </Box>
            {tagsToCreate.length > 0 && (
              <Box>
                <div className="tagsOuter">
                  {tagsToCreate.map((el, index) => (
                    <div className="tag" key={index}>
                      <p>{el}</p>
                      <button type="button">
                        <img
                          src={TemplateCrossCIcon}
                          alt={el}
                          onClick={(e) => handleChipsCross(e)}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </Box>
            )}
          </Box>
        </form>
      </Box>
    </OlcFormModal>
  );
};

export default CreateTagModal;
